import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { cardService } from '../../services/cardService';
import { vcardService } from '../../services/vcardService';
import { analyticsService } from '../../services/analyticsService';
import { CardPreview } from '../../components/editor/CardPreview';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { SaveContactInstructions } from '../../components/public/SaveContactInstructions';
import { ExchangeContactModal } from '../../components/public/ExchangeContactModal';
import { getAppearanceStyles } from '../../utils/appearance';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

export const PublicCard = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [vcardUrl, setVcardUrl] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showExchangeDialog, setShowExchangeDialog] = useState(false);
  
  const trackedOpen = useRef(false);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const result = await cardService.getPublicCardBySlug(slug);
        setData(result);
        if (result?.card) {
          document.title = `${result.card.full_name} - ${result.card.job_title} | Meetoi`;
          
          let metaDesc = document.querySelector('meta[name="description"]');
          if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.name = "description";
            document.head.appendChild(metaDesc);
          }
          metaDesc.content = result.card.bio || `Digital professional card for ${result.card.full_name}.`;

          const blob = vcardService.getVCardBlob(result.card);
          const url = URL.createObjectURL(blob);
          setVcardUrl(url);

          // Track Card Opened Safely Once
          if (!trackedOpen.current) {
            trackedOpen.current = true;
            const source = searchParams.get('source') === 'qr_code' ? 'qr' : 'direct';
            const token = searchParams.get('token');
            const metadata = token ? { token } : {};
            analyticsService.trackEvent(result.card.public_slug, 'card_opened', source, metadata).catch(console.error);
          }
        }
      } catch (err) {
        setError(true);
        document.title = "Card Unavailable | Meetoi";
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchCard();
    return () => {
      if (vcardUrl) URL.revokeObjectURL(vcardUrl);
    };
  }, [slug, searchParams]);

  const handleTrack = (eventType, metadata = {}) => {
    if (data?.card) {
      const source = searchParams.get('source') === 'qr_code' ? 'qr' : 'direct';
      const token = searchParams.get('token');
      const enrichedMetadata = token ? { ...metadata, token } : metadata;
      analyticsService.trackEvent(data.card.public_slug, eventType, source, enrichedMetadata);
    }
  };

  const handleSaveContact = () => {
    handleTrack('contact_saved');
    setShowSaveDialog(true);
  };

  const handleRetryDownload = () => {
    if (data?.card) {
      vcardService.generateAndDownload(data.card);
    }
  };

  const handleExchangeContact = () => {
    handleTrack('connection_started');
    setShowExchangeDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-slate-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4 text-center">
        <div className="w-16 h-16 bg-slate-200 text-slate-400 rounded-full flex items-center justify-center mb-6">
          <SafeIcon icon={FiIcons.FiUserX} className="text-3xl" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">This Meetoi card is unavailable</h1>
        <p className="text-slate-600 max-w-md mx-auto mb-8">
          The card may have been unpublished, archived, or the link may be incorrect.
        </p>
        <div className="flex gap-4">
          <Link to="/">
            <Button>Go to Meetoi</Button>
          </Link>
        </div>
      </div>
    );
  }

  const s = getAppearanceStyles(data.card.appearance);

  return (
    <div className={`min-h-screen ${s.pageBg} sm:py-12 flex flex-col justify-center items-center transition-colors duration-300`}>
      
      {showSaveDialog && (
        <SaveContactInstructions onRetry={handleRetryDownload} onClose={() => setShowSaveDialog(false)} />
      )}
      
      {showExchangeDialog && (
        <ExchangeContactModal 
          card={data.card} 
          sourceContext={{ source: searchParams.get('source') || 'public_card', token: searchParams.get('token') }}
          onClose={() => setShowExchangeDialog(false)} 
          onSaveOwnerContact={() => {
            handleRetryDownload();
            handleSaveContact();
          }}
          onTrack={handleTrack}
        />
      )}

      <div className={`w-full sm:max-w-[400px] shadow-none sm:shadow-2xl rounded-none ${s.cornerCard} overflow-hidden ${s.cardBg}`}>
        <CardPreview 
          card={data.card} 
          links={data.links} 
          mode="public" 
          vcardUrl={vcardUrl} 
          onSaveContact={handleSaveContact}
          onExchangeContact={handleExchangeContact}
          onTrack={handleTrack}
        />
      </div>
    </div>
  );
};